import { Link, useParams, Navigate } from "react-router-dom";
import { Phone, ArrowRight, Check, Clock, ShieldCheck, BadgeEuro, SearchCheck, PhoneCall, MapPin, BedDouble, CalendarCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, MaskedLine, SectionTag } from "@/components/Reveal";
import CtaSection from "@/components/CtaSection";
import Seo from "@/components/Seo";
import CityMap from "@/components/CityMap";
import GoogleRating from "@/components/GoogleRating";
import LiveStatus from "@/components/LiveStatus";
import GuaranteeBadge from "@/components/GuaranteeBadge";
import { CITIES, ZONES } from "@/data/cities";
import { CITY_COORDS } from "@/data/cityCoords";
import { CALENDLY_URL, SITE_URL } from "@/data/site";

const BED_BUG_IMG = "/assets/punaise.jpg";

const STATS = [
    { icon: Clock, title: "Sous 2h", desc: "Intervention express à domicile" },
    { icon: CalendarCheck, title: "7j/7", desc: "Du lundi au dimanche, 9h - 20h" },
    { icon: ShieldCheck, title: "CertiBIOCIDE", desc: "Entreprise agréée et certifiée" },
    { icon: BadgeEuro, title: "Dès 180 €", desc: "Dim. & fériés : +20 %" },
];

const WHY = [
    { icon: SearchCheck, title: "Détection fiable et traitement efficace", desc: "Nous sommes formés conformément aux recommandations du gouvernement et utilisons des produits professionnels." },
    { icon: BadgeEuro, title: "Prix justes et transparents", desc: "Une grille tarifaire respectée, appliquée uniformément tous les jours de la semaine, sans exception." },
    { icon: ShieldCheck, title: "Protection du client et garanties", desc: "Un service professionnel de qualité, avec possibilité de garantie de repassage en cas de non-fonctionnement." },
    { icon: PhoneCall, title: "Suivi avant et après l'intervention", desc: "Vous pouvez nous contacter pour obtenir des renseignements ou des conseils avant ou après l'intervention." },
];

const TREATMENTS = [
    { name: "Vapeur sèche 180°C", tag: "La plus efficace", price: "dès 350 €", features: ["100% naturelle, sans produit chimique", "Idéale : personnes âgées, enfants & animaux", "Canapé 90 €, lit 100 €", "1 seul passage"], highlight: true },
    { name: "Insecticides professionnels", tag: "L'économique", price: "dès 180 €", features: ["Pulvérisation rémanente", "Tarif par tranche de superficie", "2 passages requis"], highlight: false },
    { name: "Fumigène effet choc", tag: "Longue rémanence", price: "160 €", features: ["Efficace sur les punaises résistantes", "À partir de 10 m²", "1 seul passage"], highlight: false },
];

const STEPS = [
    { num: "01", title: "Contact", desc: "Appelez-nous ou réservez en ligne pour un pré-diagnostic immédiat et un rendez-vous planifié en toute transparence." },
    { num: "02", title: "Inspection", desc: "Un technicien certifié réalise un diagnostic complet de votre logement et vous propose le traitement le plus adapté." },
    { num: "03", title: "Traitement", desc: "Intervention possible sous 2h, 7j/7 : vapeur sèche 180°C, insecticides professionnels ou fumigène selon votre situation." },
    { num: "04", title: "Suivi", desc: "Conseils avant et après intervention, accompagnement téléphonique et garantie de repassage possible." },
];

const CityPunaises = () => {
    const { slug } = useParams();
    const city = CITIES.find((c) => c.slug === slug);
    if (!city) return <Navigate to="/punaises-de-lit" replace />;
    const zone = ZONES.find((z) => z.id === city.zone);

    const others = [
        ...CITIES.filter((c) => c.slug !== slug && c.zone === city.zone),
        ...CITIES.filter((c) => c.zone !== city.zone),
    ].slice(0, 8);
    const title = `Traitement punaises de lit à ${city.name} | Intervention sous 2h, 7j/7 | Eradikbreizh`;
    const description = `Exterminateur de punaises de lit à ${city.name}${city.aka ? ` (${city.aka})` : ""}, ${city.dept}. Intervention sous 2h, 7j/7, dès 180 € HT. Vapeur 180°C, insecticides pro, fumigène. Devis gratuit : 02 55 99 22 43.`;
    const [cityLat, cityLng] = CITY_COORDS[city.slug] || [];
    const faq = [
        {
            q: `Combien coûte un traitement contre les punaises de lit à ${city.name} ?`,
            a: `Nos interventions à ${city.name} démarrent à 180 € HT (TVA non applicable, art. 293 B du CGI) pour le traitement chimique, 350 € pour la vapeur sèche 180°C et 160 € pour le fumigène. La recherche de punaises est facturée 59 €, déduite du traitement si l'intervention est réalisée immédiatement, le jour même. Consultez notre grille complète sur la page Tarifs : sans majoration en semaine, majoration de 20 % les samedis, dimanches et jours fériés.`,
        },
        {
            q: `En combien de temps pouvez-vous intervenir à ${city.name} ?`,
            a: `Notre base est située au Mont-Dol, à environ ${city.dist} de ${city.name}${city.aka ? ` (${city.aka})` : ""}. Nous pouvons intervenir en moins de 2h, du lundi au dimanche de 9h à 20h, y compris les week-ends et jours fériés (majoration de 20 % les samedis, dimanches et jours fériés).`,
        },
        {
            q: "Le traitement est-il sans danger pour mes enfants et mes animaux ?",
            a: "Oui : notre traitement vapeur sèche 180°C est 100% naturel, sans aucun produit chimique. Il est idéal pour les enfants, les personnes âgées, les animaux et les personnes fragiles, et vous pouvez rester dans votre logement pendant l'intervention.",
        },
        {
            q: "Faut-il préparer mon logement avant votre arrivée ?",
            a: "Oui, la préparation fait partie intégrante du traitement : elle est détaillée dans notre protocole avant et après traitement, disponible sur ce site. Un logement bien préparé garantit l'efficacité du traitement et évite toute facturation de déplacement inutile.",
        },
    ];

    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Service",
                serviceType: "Traitement et extermination des punaises de lit",
                provider: {
                    "@type": "LocalBusiness",
                    name: "Eradikbreizh",
                    telephone: "+33255992243",
                    email: "contact@eradikbreizh.fr",
                    address: { "@type": "PostalAddress", addressLocality: "Mont-Dol", addressRegion: "Bretagne", addressCountry: "FR" },
                    geo: { "@type": "GeoCoordinates", latitude: 48.5713, longitude: -1.7578 },
                },
                areaServed: {
                    "@type": "City",
                    name: city.name,
                    ...(cityLat ? { geo: { "@type": "GeoCoordinates", latitude: cityLat, longitude: cityLng } } : {}),
                },
                offers: { "@type": "Offer", priceCurrency: "EUR", price: "180", description: "Traitement punaises de lit à partir de 180 € HT" },
            },
            {
                "@type": "FAQPage",
                mainEntity: faq.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
            },
            {
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
                    { "@type": "ListItem", position: 2, name: "Punaises de lit", item: `${SITE_URL}/punaises-de-lit` },
                    { "@type": "ListItem", position: 3, name: `Punaises de lit à ${city.name}` },
                ],
            },
        ],
    };

    return (
        <div data-testid="city-punaises-page">
            <Seo title={title} description={description} jsonLd={jsonLd} />

            {/* HERO */}
            <section className="relative overflow-hidden bg-gradient-to-b from-mist to-white pb-24 pt-40 md:pt-48">
                <div className="absolute -right-40 top-0 h-[30rem] w-[30rem] rounded-full bg-brand/10 blur-[160px]" aria-hidden="true" />
                <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
                    <div>
                        <MaskedLine delay={0.1}>
                            <span className="inline-flex items-center gap-3 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600 shadow-sm">
                                <MapPin className="h-4 w-4 text-brand" />
                                {city.name} · {city.dept}
                            </span>
                        </MaskedLine>
                        <h1 className="mt-8 font-display text-4xl font-black uppercase leading-[0.98] tracking-tighter sm:text-6xl">
                            <MaskedLine delay={0.25}>Punaises de lit</MaskedLine>
                            <MaskedLine delay={0.4}>
                                <span className="text-brand">à {city.name}</span>
                            </MaskedLine>
                        </h1>
                        <MaskedLine delay={0.55}>
                            <p className="mt-8 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg">{zone.intro} À {city.name} et dans les communes voisines, nos techniciens interviennent en moins de 2h, 7j/7, pour un diagnostic et un traitement complet de votre logement.</p>
                        </MaskedLine>
                        <MaskedLine delay={0.7}>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <a
                                    data-testid="city-phone-button"
                                    href="tel:+33255992243"
                                    className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-105 active:scale-95"
                                >
                                    <Phone className="h-5 w-5" />
                                    02 55 99 22 43
                                </a>
                                <Link
                                    data-testid="city-devis-button"
                                    to="/tarifs"
                                    className="inline-flex items-center gap-2 rounded-full border border-black/15 px-8 py-4 font-bold text-night transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-black/5"
                                >
                                    Voir les tarifs
                                    <ArrowRight className="h-5 w-5 text-brand" />
                                </Link>
                            </div>
                        </MaskedLine>
                        <MaskedLine delay={0.85}>
                            <div className="mt-6">
                                <LiveStatus testid="city-live-status" />
                            </div>
                        </MaskedLine>
                    </div>
                    <Reveal delay={0.3} className="relative">
                        <div className="relative overflow-hidden rounded-3xl border border-black/10 shadow-[0_30px_80px_rgba(10,15,36,0.15)]">
                            <img src={BED_BUG_IMG} alt={`Punaises de lit sur un matelas à ${city.name}`} className="h-[22rem] w-full object-cover sm:h-[26rem]" />
                            <div className="absolute inset-0 bg-gradient-to-t from-night/80 via-transparent to-transparent" />
                            <div className="absolute bottom-6 left-6 rounded-2xl border border-black/10 bg-white/85 px-5 py-4 backdrop-blur-xl">
                                <p className="font-display text-2xl font-black text-brand">Sous 2h</p>
                                <p className="text-xs uppercase tracking-widest text-slate-600">à {city.name}</p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* RÉSERVATION EN LIGNE */}
            <section className="bg-white py-20 md:py-24" data-testid="city-booking-section">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
                    <div>
                        <Reveal><SectionTag>Réservation en ligne</SectionTag></Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                                Réservez votre inspection <span className="text-brand">à {city.name} en 2 min</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600">
                                Pas d'attente ni d'allers-retours téléphoniques : choisissez directement votre créneau en ligne et on se déplace chez vous, à {city.name}, pour inspecter votre logement.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <ul className="mt-8 space-y-4">
                                {[
                                    "Créneau choisi par vous, 7j/7 de 9h à 20h",
                                    "Inspection et diagnostic à domicile : 59 €, déduits si le traitement est réalisé le jour même",
                                    "Confirmation immédiate de votre rendez-vous",
                                ].map((item) => (
                                    <li key={item} className="flex items-start gap-3 text-sm font-medium text-night sm:text-base">
                                        <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                    <Reveal delay={0.25}>
                        <div className="rounded-3xl border border-brand/30 bg-brand/5 p-8 text-center sm:p-10">
                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand text-white">
                                <CalendarCheck className="h-8 w-8" />
                            </div>
                            <h3 className="mt-6 font-display text-2xl font-black uppercase tracking-tight">Réservez en ligne en 2 min</h3>
                            <p className="mt-3 text-sm leading-relaxed text-slate-600">
                                Choisissez votre créneau directement sur notre agenda en ligne. On s'occupe du reste.
                            </p>
                            <a
                                data-testid="city-booking-button"
                                href={CALENDLY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-8 py-4 font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-105 active:scale-95 sm:w-auto"
                            >
                                <CalendarCheck className="h-5 w-5" />
                                Choisir mon créneau
                            </a>
                            <p className="mt-4 text-xs uppercase tracking-widest text-slate-500">Inspection à domicile : 59 €</p>
                            <p className="mt-4 text-xs leading-relaxed text-slate-500">
                                Lors de la réservation, indiquez le numéro de téléphone auquel nous pouvons vous joindre : nous pourrons par exemple vous envoyer un SMS pour vous rappeler le rendez-vous. Vous pouvez à tout moment vous inscrire gratuitement sur la liste d'opposition au démarchage téléphonique BLOCTEL.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* STATS */}
            <section className="bg-brand py-14">
                <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
                    {STATS.map((s, i) => (
                        <Reveal key={s.title} delay={0.08 * i} className="flex items-start gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white">
                                <s.icon className="h-6 w-6" />
                            </div>
                            <div>
                                <p className="font-display text-2xl font-black uppercase tracking-tight text-white">{s.title}</p>
                                <p className="mt-1 text-sm text-white/75">{s.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* ARTISAN LOCAL */}
            <section className="bg-white py-24 md:py-28" data-testid="city-artisan-section">
                <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
                    <Reveal><SectionTag>Votre artisan local</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            Un expert local <span className="text-brand">à {city.name}</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="relative mx-auto mt-14 max-w-2xl">
                            <div className="absolute -left-8 -top-8 hidden h-24 w-24 rounded-full bg-brand/10 blur-2xl sm:block" aria-hidden="true" />
                            <div className="overflow-hidden rounded-3xl border border-black/10 bg-white p-6 shadow-[0_20px_60px_rgba(10,15,36,0.12)] sm:p-10">
                                <img
                                    data-testid="city-artisan-photo"
                                    src="/assets/technicien-van.webp"
                                    alt={`Votre technicien Eradikbreizh et son véhicule d'intervention à ${city.name}`}
                                    className="mx-auto w-full"
                                />
                            </div>
                            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 rounded-full border border-black/10 bg-white px-6 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-night shadow-lg">
                                Votre interlocuteur unique, 7j/7
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <p className="mx-auto mt-14 max-w-2xl text-base leading-relaxed text-slate-600">
                            Basé au Mont-Dol, à environ {city.dist} de {city.name}, Eradikbreizh est une entreprise locale et indépendante. Pas de plateforme ni de sous-traitance : vous échangez directement avec le technicien certifié qui intervient à votre domicile, du diagnostic jusqu'au suivi après traitement.
                        </p>
                    </Reveal>
                    {city.local && (
                        <Reveal delay={0.33}>
                            <p data-testid="city-local-text" className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-600">
                                {city.local}
                            </p>
                        </Reveal>
                    )}
                    <Reveal delay={0.35}>
                        <div className="mt-10 flex flex-wrap justify-center gap-4">
                            <a
                                data-testid="city-artisan-phone-button"
                                href="tel:+33255992243"
                                className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-105 active:scale-95"
                            >
                                <Phone className="h-5 w-5" />
                                02 55 99 22 43
                            </a>
                            <Link
                                data-testid="city-artisan-devis-button"
                                to="/tarifs"
                                className="inline-flex items-center gap-2 rounded-full border border-black/15 px-8 py-4 font-bold text-night transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-black/5"
                            >
                                Voir les tarifs
                                <ArrowRight className="h-5 w-5 text-brand" />
                            </Link>
                        </div>
                    </Reveal>
                    <Reveal delay={0.4} className="mt-12">
                        <div className="mx-auto max-w-3xl text-left">
                            <GoogleRating testidPrefix="city-google" />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* WHY */}
            <section className="bg-mist py-24 md:py-28">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal><SectionTag>Pourquoi Eradikbreizh</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 max-w-3xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            Les meilleurs traitements pour <span className="text-brand">en finir avec les punaises de lit à {city.name}</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
                            Les punaises de lit peuvent causer de nombreux désagréments dans nos foyers. Ces petits insectes nocturnes se nourrissent de notre sang et provoquent démangeaisons et irritations cutanées. Si vous êtes confronté à une infestation, ne désespérez pas : nous vous proposons des traitements efficaces pour éliminer ces nuisibles de votre logement.
                        </p>
                    </Reveal>
                    <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                        {WHY.map((w, i) => (
                            <Reveal key={w.title} delay={0.08 * i} className="h-full">
                                <div className="h-full rounded-3xl border border-black/10 bg-white p-8 shadow-sm transition-colors duration-500 hover:border-brand/40">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                                        <w.icon className="h-6 w-6" />
                                    </div>
                                    <h3 className="mt-6 font-display text-xl font-bold">{w.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{w.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* TREATMENTS */}
            <section className="bg-white py-24 md:py-28">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal><SectionTag>Nos traitements</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 max-w-2xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            3 traitements contre les <span className="text-brand">punaises de lit à {city.name}</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="mt-6">
                        <GuaranteeBadge testid="city-guarantee-badge" />
                        <Link to="/cgv" data-testid="city-guarantee-cgv-link" className="mt-2 block text-xs text-slate-400 underline-offset-4 transition-colors hover:text-brand hover:underline">
                            Selon conditions de garantie, voir nos CGV
                        </Link>
                    </Reveal>
                    <div className="mt-14 grid gap-8 lg:grid-cols-3">
                        {TREATMENTS.map((t, i) => (
                            <Reveal key={t.name} delay={0.1 * i} className="h-full">
                                <div
                                    data-testid={`city-treatment-${i + 1}`}
                                    className={`flex h-full flex-col rounded-3xl border p-8 transition-[border-color,transform] duration-500 hover:-translate-y-2 ${
                                        t.highlight
                                            ? "border-brand bg-brand text-white shadow-[0_20px_60px_rgba(27,67,189,0.35)]"
                                            : "border-black/10 bg-white shadow-sm hover:border-brand/40"
                                    }`}
                                >
                                    <span className={`inline-flex w-fit rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest ${t.highlight ? "bg-white text-brand" : "bg-brand/10 text-brand"}`}>
                                        {t.tag}
                                    </span>
                                    <h3 className={`mt-5 font-display text-2xl font-black uppercase tracking-tight ${t.highlight ? "text-white" : "text-night"}`}>{t.name}</h3>
                                    <p className={`mt-3 font-display text-3xl font-black ${t.highlight ? "text-white" : "text-brand"}`}>{t.price}</p>
                                    <ul className="mt-6 flex-1 space-y-3">
                                        {t.features.map((f) => (
                                            <li key={f} className={`flex items-start gap-3 text-sm ${t.highlight ? "text-white/85" : "text-slate-600"}`}>
                                                <Check className={`mt-0.5 h-4 w-4 shrink-0 ${t.highlight ? "text-white" : "text-brand"}`} />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <a
                                        data-testid={`city-treatment-${i + 1}-booking-button`}
                                        href={CALENDLY_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-[background-color,transform] duration-300 hover:scale-[1.03] active:scale-95 ${
                                            t.highlight
                                                ? "bg-white text-brand hover:bg-slate-100"
                                                : "bg-brand text-white hover:bg-brand-hover"
                                        }`}
                                    >
                                        <CalendarCheck className="h-4 w-4" />
                                        Réserver en ligne en 2 min
                                    </a>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.25} className="mt-10">
                        <Link
                            data-testid="city-tarifs-button"
                            to="/tarifs"
                            className="inline-flex items-center gap-2 rounded-full border border-black/15 px-8 py-4 font-bold text-night transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-black/5"
                        >
                            Voir la grille complète des tarifs 2026
                            <ArrowRight className="h-5 w-5 text-brand" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* STEPS */}
            <section className="bg-mist py-24 md:py-28">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal><SectionTag>Le processus</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            4 étapes vers <span className="text-brand">la sérénité</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-8 md:grid-cols-2">
                        {STEPS.map((step, i) => (
                            <Reveal key={step.num} delay={0.08 * i}>
                                <div className="group relative overflow-hidden rounded-3xl border border-black/10 bg-white p-8 shadow-sm transition-colors duration-500 hover:border-brand/50 sm:p-10">
                                    <span className="absolute -right-4 -top-8 font-display text-[8rem] font-black leading-none text-night/5 transition-colors duration-500 group-hover:text-brand/15">
                                        {step.num}
                                    </span>
                                    <p className="text-xs font-bold uppercase tracking-[0.3em] text-brand">Étape {step.num}</p>
                                    <h3 className="mt-3 font-display text-2xl font-black uppercase tracking-tight">{step.title}</h3>
                                    <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">{step.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-white py-24 md:py-28">
                <div className="mx-auto max-w-4xl px-5 sm:px-8">
                    <Reveal><SectionTag>Questions fréquentes</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            Punaises de lit à {city.name} : <span className="text-brand">vos questions</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2} className="mt-12">
                        <Accordion type="single" collapsible className="space-y-4" data-testid="city-faq">
                            {faq.map((item, i) => (
                                <AccordionItem
                                    key={i}
                                    value={`item-${i}`}
                                    data-testid={`city-faq-item-${i + 1}`}
                                    className="rounded-2xl border border-black/10 bg-white px-6 shadow-sm transition-colors data-[state=open]:border-brand/40"
                                >
                                    <AccordionTrigger className="py-6 text-left font-display text-lg font-bold hover:no-underline">
                                        {item.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="pb-6 text-sm leading-relaxed text-slate-600 sm:text-base">
                                        {item.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>
                </div>
            </section>

            {/* OTHER CITIES + CARTE */}
            <section className="bg-mist py-20">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal><SectionTag>Zone d'intervention</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-display text-3xl font-black uppercase tracking-tight sm:text-4xl">
                            Nous intervenons à {city.name} <span className="text-brand">et près de chez vous</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="mt-10">
                        <CityMap key={city.slug} city={city} others={others} />
                    </Reveal>
                    <Reveal delay={0.2} className="mt-10 flex flex-wrap gap-3">
                        {others.map((c) => (
                            <Link
                                key={c.slug}
                                data-testid={`city-link-${c.slug}`}
                                to={`/punaises-de-lit/${c.slug}`}
                                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-sm font-semibold text-night shadow-sm transition-[border-color,color] duration-300 hover:border-brand hover:text-brand"
                            >
                                <BedDouble className="h-4 w-4 text-brand" />
                                Punaises de lit {c.name}
                            </Link>
                        ))}
                    </Reveal>
                </div>
            </section>

            {/* HOTELS */}
            <section className="bg-night py-20 md:py-24" data-testid="city-hotel-section">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <div className="grid items-center gap-12 lg:grid-cols-2">
                        <div>
                            <Reveal>
                                <p className="text-xs font-bold uppercase tracking-[0.3em] text-white/60">Professionnels de l'hébergement</p>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <h2 className="mt-6 font-display text-3xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-4xl">
                                    Vous gérez un hôtel à {city.name} ?
                                </h2>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <p className="mt-6 text-sm leading-relaxed text-white/80 sm:text-base">
                                    Intervention en toute discrétion avec un véhicule blanc banalisé, de 8h à 22h, 7j/7. Traitement vapeur 180°C sans produit chimique dès 190 € HT par chambre : la chambre est relouable le jour même, et vous recevez un rapport écrit pour répondre à tout avis client.
                                </p>
                            </Reveal>
                            <Reveal delay={0.2}>
                                <div className="mt-8 flex flex-wrap gap-4">
                                    <Link
                                        data-testid="city-hotel-link"
                                        to="/hotels"
                                        className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-bold text-brand transition-[background-color,transform] duration-300 hover:bg-slate-100 hover:scale-105 active:scale-95"
                                    >
                                        Découvrir l'offre hôtels
                                        <ArrowRight className="h-5 w-5" />
                                    </Link>
                                    <a
                                        data-testid="city-hotel-phone-button"
                                        href="tel:+33255992243"
                                        className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-8 py-4 font-bold text-white transition-[background-color,border-color] duration-300 hover:border-white hover:bg-white/10"
                                    >
                                        <Phone className="h-5 w-5" />
                                        02 55 99 22 43
                                    </a>
                                </div>
                            </Reveal>
                        </div>
                        <Reveal delay={0.1}>
                            <img
                                data-testid="city-hotel-photo"
                                src="/assets/camion-banalise.jpg"
                                alt={`Véhicule blanc banalisé Eradikbreizh pour des interventions discrètes dans les hôtels de ${city.name}`}
                                className="w-full rounded-3xl border border-white/15 object-cover shadow-2xl"
                                loading="lazy"
                            />
                        </Reveal>
                    </div>
                </div>
            </section>

            <CtaSection
                title={`Des punaises de lit à ${city.name} ?`}
                subtitle="Chaque nuit compte. Appelez-nous pour un diagnostic et une intervention sous 2h, 7j/7."
            />
        </div>
    );
};

export default CityPunaises;
